import { Flex, Text } from "styles/utility-components";

import Button from "components/ui/button/Button";
import Modal from "components/ui/modal/Modal";
import toast from "components/ui/toast/Toast";
import type { McpClient, McpClientWithSecret } from "lib/hooks/api/auth/mcp/schemas";
import { useRotateMcpClientSecret } from "lib/hooks/api/auth/mcp/useRotateMcpClientSecret";

type Props = {
  credential: McpClient;
  onClose: () => void;
  onRotated: (credential: McpClientWithSecret) => void;
};

const BODY_ID = "rotate-mcp-secret-modal-body";

/**
 * Confirms before rotating. The old secret stops working the moment the new one is issued, so a
 * misclick takes a working integration down until someone pastes the replacement in.
 */
export function RotateSecretModal({ credential, onClose, onRotated }: Props) {
  const { mutate: rotateSecret, isLoading } = useRotateMcpClientSecret({
    onSuccess: (rotated) => onRotated(rotated),
    onError: (error) => toast.error(error.message ?? "Could not rotate the secret"),
  });

  return (
    <Modal
      open
      onOpenChange={onClose}
      maxWidth="440px"
      showCloseButton={false}
      aria-describedby={BODY_ID}
      padding={24}
    >
      <Flex column gap={24}>
        <Flex column gap={8}>
          <Text h2>Rotate secret</Text>
          <Text id={BODY_ID}>
            The current secret for {credential.client_name ?? "this credential"} stops working
            straight away. Your AI tool will keep failing to sign in until you paste the new one in,
            so do this when you can finish the job.
          </Text>
        </Flex>
        <Flex justifyContent="flex-end" gap={8}>
          <Button buttonStyle="inverse" onClick={onClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            onClick={() => rotateSecret({ clientId: credential.client_id })}
            disabled={isLoading}
            loading={isLoading}
          >
            Rotate
          </Button>
        </Flex>
      </Flex>
    </Modal>
  );
}
